import type { MaxChatMessage } from '../types'

const LOG_PREFIX = '[MAX Message Debug]'

const redact = (value: string | null | undefined): string => {
  if (!value) return 'none'
  if (value.length <= 12) return '***'
  return `${value.slice(0, 6)}...${value.slice(-4)}`
}

export const maxDebug = {
  debug: (_message: string, _payload?: Record<string, unknown>) => {},
  info: (_message: string, _payload?: Record<string, unknown>) => {},
  warn: (_message: string, _payload?: Record<string, unknown>) => {},
  error: (_message: string, _payload?: Record<string, unknown>) => {},
  token: redact,
}

type MessageSnapshot = {
  index: number
  id: string
  messageId: string | null
  requestId: string | null
  role: string
  conversationId: string
  createdAt: number
  createdAtIso: string | null
  createdAtValid: boolean
  responseType: string | null
  isFlow: boolean
  preview: string
}

const previewContent = (value: string): string => {
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (normalized.length <= 80) {
    return normalized
  }

  return `${normalized.slice(0, 77)}...`
}

const toIsoOrNull = (value: number): string | null => {
  if (!Number.isFinite(value)) {
    return null
  }

  try {
    return new Date(value).toISOString()
  } catch {
    return null
  }
}

const snapshotMessages = (messages: MaxChatMessage[]): MessageSnapshot[] =>
  messages.map((message, index) => ({
    index,
    id: message.id,
    messageId: message.messageId || null,
    requestId: message.requestId || null,
    role: message.role,
    conversationId: message.conversationId,
    createdAt: message.createdAt,
    createdAtIso: toIsoOrNull(message.createdAt),
    createdAtValid: Number.isFinite(message.createdAt),
    responseType: message.responseType || null,
    isFlow: Boolean(message.isFlow),
    preview: previewContent(message.content || ''),
  }))

const log = (level: 'debug' | 'info' | 'warn' | 'error', message: string, payload?: unknown) => {
  const fullMessage = `${LOG_PREFIX} ${message}`
  if (level === 'error') {
    if (payload !== undefined) {
      console.error(fullMessage, payload)
      return
    }
    console.error(fullMessage)
    return
  }

  if (level === 'warn') {
    if (payload !== undefined) {
      console.warn(fullMessage, payload)
      return
    }
    console.warn(fullMessage)
    return
  }

  if (level === 'debug') {
    if (payload !== undefined) {
      console.debug(fullMessage, payload)
      return
    }
    console.debug(fullMessage)
    return
  }

  if (payload !== undefined) {
    console.log(fullMessage, payload)
    return
  }
  console.log(fullMessage)
}

export const maxMessageDebug = {
  snapshotMessages,
  event: (message: string, payload?: unknown) => log('info', message, payload),
  state: (
    message: string,
    payload: {
      conversationId?: string | null
      messages?: MaxChatMessage[]
      [key: string]: unknown
    }
  ) => {
    const { messages, ...rest } = payload
    log('info', message, {
      ...rest,
      order: messages ? snapshotMessages(messages) : undefined,
    })
  },
  ui: (
    message: string,
    payload: {
      conversationId?: string | null
      messages?: MaxChatMessage[]
      [key: string]: unknown
    }
  ) => {
    const { messages, ...rest } = payload
    log('info', message, {
      ...rest,
      renderedOrder: messages ? snapshotMessages(messages) : undefined,
    })
  },
  error: (message: string, payload?: unknown) => log('error', message, payload),
}
